import Joi from 'joi';
import { useSnackbar } from 'notistack';
import { useContext } from 'react';
import { useQuery } from 'react-query';
import { SettingsContext } from '../store/SettingsContext';
import { UserContext } from '../store/UserContext';
import { OnlyFansLists } from '../types/onlyFansTypes';
import useAxios from './useAxios';
import { SextforceAutoRepostColor } from './useSextforceAutoRepost';
import useSubscriber from './useSubscriber';

export const arraySextforceAutoMassDmMessageStatus = ['sent', 'scheduled', 'unsent'] as const;
export type SextforceAutoMassDmMessageStatus = typeof arraySextforceAutoMassDmMessageStatus[number];

export interface SextforceAutoMassDmMessage {
    _id?: string;
    subscriberId: string;
    massDmId: number;
    scheduleId?: string;
    schedule?: OnlyFansMassDMMessageScheduleDocument;
    name?: string;
    colorId?: string;
    date: Date;
    scheduledAt?: Date;
    status: SextforceAutoMassDmMessageStatus;
    sentCount: number;
    viewedCount: number;
    canUnsend: boolean;
    purchasedCount: number;
    isCancelled: boolean;
    isFree: boolean;
    canSendMessageToBuyers: boolean;
    price: number;
    text: string;
    rawText: string;
    vaultMediaId?: number;
    inventoryId?: number;
    versionId?: number;
    messageDetails?: any;
    createdAt?: Date;
    updatedAt?: Date;
}

export const arraySextforceAutoMassDmMessageListSortBy = ['date', 'name', 'sentCount', 'viewedCount', 'purchasedCount'];
export type SextforceAutoMassDmMessageListSortBy = typeof arraySextforceAutoMassDmMessageListSortBy[number];

export namespace OnlyFansMessaMessagesQueueDetails {
    export interface Response {
        responseType: string;
        id: number;
        text: string;
        rawText: string;
        giphyId: null;
        lockedText: boolean;
        isNew: boolean;
        isFree: boolean;
        isOpened: boolean;
        price: number;
        isMediaReady: boolean;
        mediaCount: number;
        media: Media[];
        previews: number[];
        createdAt: Date;
        changedAt: Date;
        scheduledAt?: Date | null;
        isTip: boolean;
        cancelSeconds: number;
        isLiked: boolean;
        canPurchase: boolean;
        isMarkdownDisabled: boolean;
        releaseForms: ReleaseForm[];
        userLists?: (number | string)[];
        excludedLists?: (number | string)[];
        fromUser: FromUser;
        isFromQueue: boolean;
        queueId: number;
        canUnsendQueue: boolean;
        unsendSecondsQueue: number;
    }

    export enum View {
        N = 'n',
        M = 'm',
        Cl = 'cl',
        X = 'x',
        S = 's',
    }

    export interface ReleaseForm {
        id: number;
        name: string;
        partnerSource: PartnerSource;
        type: ReleaseFormType;
        user: User;
    }

    export interface User {
        view: View;
        id: number;
        name: string;
        username: string;
        isVerified: boolean;
        avatar: string;
        avatarThumbs: AvatarThumbs;
        ivStatus: IvStatus;
        isFromGuest: boolean;
    }

    export interface IvStatus {
        approved: boolean;
    }

    export enum ReleaseFormType {
        Document = 'document',
        User = 'user',
    }

    export enum PartnerSource {
        Document = 'document',
        Tag = 'tag',
    }

    export interface FromUser {
        view: string;
        avatar: string;
        avatarThumbs: AvatarThumbs;
        header: string;
        headerSize: HeaderSize;
        headerThumbs: HeaderThumbs;
        id: number;
        name: string;
        username: string;
        canLookStory: boolean;
        canCommentStory: boolean;
        hasNotViewedStory: boolean;
        isVerified: boolean;
        canPayInternal: boolean;
        hasScheduledStream: boolean;
        hasStream: boolean;
        hasStories: boolean;
        tipsEnabled: boolean;
        tipsTextEnabled: boolean;
        tipsMin: number;
        tipsMinInternal: number;
        tipsMax: number;
        canEarn: boolean;
        canAddSubscriber: boolean;
        subscribePrice: number;
        hasStripe: boolean;
        isStripeExist: boolean;
        subscriptionBundles: SubscriptionBundle[];
        canSendChatToAll: boolean;
        creditsMin: number;
        creditsMax: number;
        isPaywallPassed: boolean;
        creditsMinAlternatives: number;
        canCreateLists: boolean;
        lastSeen: Date;
        canReport: boolean;
        subscribedOn: null;
    }

    export interface SubscriptionBundle {
        id: number;
        discount: number;
        duration: number;
        price: number;
        canBuy: boolean;
    }

    export interface AvatarThumbs {
        c50: string;
        c144: string;
    }

    export interface HeaderSize {
        width: number;
        height: number;
    }

    export interface HeaderThumbs {
        w480: string;
        w760: string;
    }

    export interface Media {
        id: number;
        type: string;
        convertedToVideo: boolean;
        canView: boolean;
        hasError: boolean;
        createdAt: Date;
        isReady: boolean;
        files: Files;
        duration: number;
        releaseForms: ReleaseForm[];
        hasCustomPreview: boolean;
        videoSources: { [key: string]: null };
    }

    export interface Files {
        full: Full;
        thumb: Full;
        preview: Full;
        squarePreview: Full;
        drm?: DRM;
    }

    export interface DRM {
        manifest: Manifest;
        signature: Signature;
    }

    export interface Manifest {
        hls: string;
        dash: string;
    }

    export interface Signature {
        hls: Dash;
        dash: Dash;
    }

    export interface Dash {
        'CloudFront-Policy': string;
        'CloudFront-Signature': string;
        'CloudFront-Key-Pair-Id': string;
    }

    export interface Full {
        url: null | string;
        width: number;
        height: number;
        size: number;
        sources?: Source[];
        options?: Option[];
    }

    export interface Option {
        id: number;
        url: string;
        selected: boolean;
        custom: boolean;
    }

    export interface Source {
        source: string;
        width: number;
        height: number;
        size: number;
        duration: number;
    }
}

export namespace OnlyFansMassDM {
    export interface Request {
        isForward: boolean;
        lockedText: boolean;
        mediaFiles: number[];
        previews: number[];
        price: number;
        rfGuest: number[];
        rfPartner: number[];
        rfTag: number[];
        text: string;
        userLists: (string | number)[];
        excludedLists: (string | number)[];
    }
}

export const OnlyFansMassDMRequestSchema = Joi.object<OnlyFansMassDM.Request>({
    isForward: Joi.boolean().required(),
    lockedText: Joi.boolean().required(),
    mediaFiles: Joi.array().items(Joi.number()).required(),
    previews: Joi.array().items(Joi.number()).required(),
    price: Joi.number().required(),
    rfGuest: Joi.array().items(Joi.number()).required(),
    rfPartner: Joi.array().items(Joi.number()).required(),
    rfTag: Joi.array().items(Joi.number()).required(),
    text: Joi.string().required(),
    userLists: Joi.array().items(Joi.alternatives(Joi.string(), Joi.number())).required(),
    excludedLists: Joi.array().items(Joi.alternatives(Joi.string(), Joi.number())).required(),
});

export const arrayOnlyFansMassDMMessageScheduleType = ['manual', 'album', 'template', 'caption'] as const;

export type OnlyFansMassDMMessageScheduleType = typeof arrayOnlyFansMassDMMessageScheduleType[number];

export interface OnlyFansMassDMMessage {
    userLists: (number | string | OnlyFansLists.Type)[];
    excludedLists: (number | string | OnlyFansLists.Type)[];
    price: number | null;
    lockedText: boolean;
    text: string;
    medias: number[];
    mediasPreviews: number[];
    taggedCreators: Array<{ id: number; name: string }>;
    taggedReleaseForms: Array<{ id: number; name: string }>;
    recentSendToListMinusDays?: number | null;
}

export interface OnlyFansMassDMMessageSchedule extends OnlyFansMassDMMessage {
    name: string;
    active: boolean;
    inactive?: boolean;
    colorId?: string;
    scheduleDate: Date | null;
    autoUnsendTimerPeriod?: string;
    autoUnsendTimerPeriodAmount?: number;
    autoUnsendViews?: number;
    autoUnsendBeforeRepeat?: boolean;
    saveTemplateName: string | null;
    repeatEveryAmount?: number;
    repeatEveryUnit?: moment.unitOfTime.DurationConstructor;
    repeatEndDate?: Date;
    repeatCount?: number;
    addPurchasersToUserList?: boolean;
    addPurchasersToUserListId?: number;
    type?: OnlyFansMassDMMessageScheduleType;
    sourceAlbumId?: number;
    postCaption?: boolean;
    captionCategory?: string;
    postImage?: boolean;
    postVideo?: boolean;
    postAudio?: boolean;
    postGif?: boolean;
    templateIds?: Array<{
        inventoryId: number;
        versionId: number;
    }>;
    /** Template mode: tag pool resolved at send time (mutually exclusive with templateIds) */
    templateTags?: string[];
    /** Tags mode: only include templates with a price (default true) */
    templateTagsPaidOnly?: boolean;
}

export interface OnlyFansMassDMMessageScheduleDocument extends OnlyFansMassDMMessageSchedule {
    _id: string;
    subscriberId: string;
    createdAt: Date;
    updatedAt?: Date;
    __v?: number;
}

export interface OnlyFansMassDMMessageScheduleDocumentWithColor extends OnlyFansMassDMMessageScheduleDocument {
    color?: SextforceAutoRepostColor;
}

export const OnlyFansMassDMMessageScheduleSchema = Joi.object<OnlyFansMassDMMessageScheduleDocumentWithColor>({
    _id: Joi.string().optional(),
    subscriberId: Joi.string().optional(),
    name: Joi.string().trim().required().default(''),
    active: Joi.boolean().default(true),
    inactive: Joi.boolean().optional().default(false),
    colorId: Joi.string().optional(),
    scheduleDate: Joi.date().allow(null),
    userLists: Joi.when('type', {
        is: 'template',
        then: Joi.array().items(Joi.alternatives().try(Joi.number(), Joi.string())).optional().default([]),
        otherwise: Joi.array().items(Joi.alternatives().try(Joi.number(), Joi.string())).required().min(1),
    }),
    excludedLists: Joi.when('type', {
        is: 'template',
        then: Joi.array().items(Joi.alternatives().try(Joi.number(), Joi.string())).optional().default([]),
        otherwise: Joi.array().items(Joi.alternatives().try(Joi.number(), Joi.string())).required(),
    }),
    price: Joi.number().allow(null),
    lockedText: Joi.boolean().default(false),
    text: Joi.string().trim().allow('').default(''),
    medias: Joi.array().items(Joi.number()).default([]),
    mediasPreviews: Joi.array().items(Joi.number()).default([]),
    taggedCreators: Joi.array()
        .items(
            Joi.object({
                id: Joi.number(),
                name: Joi.string(),
            }),
        )
        .default([]),
    taggedReleaseForms: Joi.array()
        .items(
            Joi.object({
                id: Joi.number(),
                name: Joi.string(),
            }),
        )
        .default([]),
    recentSendToListMinusDays: Joi.number().allow(null).optional(),
    autoUnsendTimerPeriod: Joi.string().allow(null).optional(),
    autoUnsendTimerPeriodAmount: Joi.number().allow(null).optional(),
    autoUnsendViews: Joi.number().allow(null).optional().min(1),
    autoUnsendBeforeRepeat: Joi.boolean().optional().default(false),
    saveTemplateName: Joi.string().allow(null),
    repeatEveryAmount: Joi.number().allow(null).optional().min(1),
    repeatEveryUnit: Joi.string().allow(null).optional(),
    repeatEndDate: Joi.date().allow(null).optional(),
    repeatCount: Joi.number().default(0).optional(),
    addPurchasersToUserList: Joi.boolean().default(false).optional().default(false),
    addPurchasersToUserListId: Joi.number().allow(null).optional(),
    type: Joi.string().valid('manual', 'album', 'template', 'caption').optional().default('manual'),
    sourceAlbumId: Joi.number().optional(),
    postCaption: Joi.boolean().optional().default(false),
    captionCategory: Joi.string().trim().optional().allow('').default(''),
    postImage: Joi.boolean().optional().default(false),
    postVideo: Joi.boolean().optional().default(false),
    postAudio: Joi.boolean().optional().default(false),
    postGif: Joi.boolean().optional().default(false),
    templateIds: Joi.when('type', {
        is: 'template',
        then: Joi.when('templateTags', {
            is: Joi.array().min(1).required(),
            then: Joi.array()
                .items(
                    Joi.object({
                        inventoryId: Joi.number().required(),
                        versionId: Joi.number().required(),
                    }),
                )
                .optional()
                .default([]),
            otherwise: Joi.array()
                .items(
                    Joi.object({
                        inventoryId: Joi.number().required(),
                        versionId: Joi.number().required(),
                    }),
                )
                .min(1)
                .required(),
        }),
        otherwise: Joi.array()
            .items(
                Joi.object({
                    inventoryId: Joi.number().required(),
                    versionId: Joi.number().required(),
                }),
            )
            .optional()
            .default([]),
    }),
    templateTags: Joi.array().items(Joi.string().trim().min(1)).optional().default([]),
    templateTagsPaidOnly: Joi.boolean().optional().default(true),
    __v: Joi.number().optional(),
    createdAt: Joi.date().optional(),
    updatedAt: Joi.date().optional(),
});

export const emptyOnlyFansMassDMMessageRequest: OnlyFansMassDMMessageSchedule = {
    type: 'manual',
    name: '',
    colorId: undefined,
    active: true,
    scheduleDate: null,
    saveTemplateName: null,
    repeatEveryAmount: undefined,
    repeatEveryUnit: undefined,
    autoUnsendTimerPeriod: undefined,
    autoUnsendTimerPeriodAmount: undefined,
    autoUnsendViews: undefined,
    repeatEndDate: undefined,
    addPurchasersToUserList: false,
    addPurchasersToUserListId: undefined,
    // Message
    userLists: [],
    excludedLists: [],
    price: null,
    lockedText: false,
    text: '',
    medias: [],
    mediasPreviews: [],
    taggedCreators: [],
    taggedReleaseForms: [],
    recentSendToListMinusDays: null,
    postImage: true,
    postVideo: true,
    postAudio: false,
    postGif: false,
    postCaption: false,
    captionCategory: '',
    templateIds: [],
    templateTags: [],
    templateTagsPaidOnly: true,
};

const useMessageUsersMassDmList = (
    searchQuery: string,
    status: SextforceAutoMassDmMessageStatus,
    offset: number,
    limit: number,
    sortBy: SextforceAutoMassDmMessageListSortBy,
) => {
    const userContext = useContext(UserContext);
    const settingsContext = useContext(SettingsContext);
    const { data: subscriber } = useSubscriber();
    const axios = useAxios();
    const { enqueueSnackbar } = useSnackbar();

    // Fetch Subscriber's Mass DMs
    const fetchMassDms = async (): Promise<{ list: SextforceAutoMassDmMessage[]; total: number; skip: number; limit: number }> => {
        const query: string = `${settingsContext.routes.massDm.base}${subscriber._id}/list?query=${searchQuery}&status=${status}&offset=${offset}&limit=${limit}&sortBy=${sortBy}`;

        return axios
            .get(query)
            .then(response => response.data)
            .catch(error => {
                console.error(error);

                enqueueSnackbar('Failed to fetch Mass DMs', { variant: 'error' });

                return { list: [], total: 0, skip: offset, limit };
            });
    };

    const subscriberLists = useQuery(
        ['subscriberMassDms', subscriber?._id, searchQuery, status, offset, limit, sortBy],
        () => fetchMassDms(),
        {
            refetchOnWindowFocus: false,
            // 5 minutes
            staleTime: 1000 * 60 * 5,
            keepPreviousData: true,
            enabled: userContext.jwtToken && settingsContext.apiKey && subscriber ? true : false,
        },
    );

    return { ...subscriberLists };
};

export default useMessageUsersMassDmList;
